import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from '@fullcalendar/daygrid'
import {BaseCard} from "../../Components/Cards/BaseCard";
import timeGridPlugin from '@fullcalendar/timegrid'
import DatePicker, {registerLocale} from "react-datepicker";
import nl from 'date-fns/locale/nl';
import fr from 'date-fns/locale/fr';
import {useEffect, useRef, useState} from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faChevronLeft, faChevronRight, faChevronDown, faChevronUp} from "@fortawesome/free-solid-svg-icons";
import {useNavigate} from "react-router-dom";
import {useBoundStore} from "../../Store/store";
import dayjs from "dayjs";
import {CreateAvailabilityDialog} from "../../Components/Dialogs/Availabilities/CreateAvailabilityDialog";
import * as React from "react";
import {UpdateAvailabilityDialog} from "../../Components/Dialogs/Availabilities/UpdateAvailabilityDialog";
import nlLocale from "@fullcalendar/core/locales/nl";
import frLocale from "@fullcalendar/core/locales/fr";
import {useTranslation} from "react-i18next";

export const MyAvailabilities = () => {
    const calendarRef = useRef(null);
    const navigate = useNavigate();
    const {t, i18n} = useTranslation();

    const currentLang = i18n.language || 'nl';
    dayjs.locale(currentLang);
    registerLocale('nl', nl);
    registerLocale('fr', fr);

    const availabilities = useBoundStore((state) => state.availabilities);
    const availabilitiesShifts = useBoundStore((state) => state.availabilitiesShifts);
    const getAvailabilitiesAndShifts = useBoundStore((state) => state.getAvailabilitiesAndShifts);
    const user = useBoundStore((state) => state.user);

    const [date, setDate] = useState(dayjs());
    const [open, setOpen] = useState(false);
    const [openEdit, setOpenEdit] = useState(false);
    const [availabilityObjectToEdit, setAvailabilityObjectToEdit] = useState(null);
    const [visibleDate, setVisibleDate] = useState(dayjs());
    const [isCalendarCollapsed, setIsCalendarCollapsed] = useState(window.innerWidth < 768);

    useEffect(() => {
        getAvailabilitiesAndShifts(visibleDate.unix());
    }, [getAvailabilitiesAndShifts, visibleDate]);

    useEffect(() => {
        const interval = setInterval(() => {
            getAvailabilitiesAndShifts(visibleDate.unix());
        }, 60*1000);
        return () => clearInterval(interval);
    }, [getAvailabilitiesAndShifts, visibleDate]);

    const transformedAvailabilities = availabilities.map((availability) => {
        return {
            id: availability.id,
            title: t('availabilities.available'),
            start: availability.start_time,
            end: availability.end_time,
            color: 'lightblue'
        }
    });

    const transformedShifts = availabilitiesShifts.map((shift) => {
        let phaseColor = 'lightblue';

        let title = '';
        if(shift?.branch) {
            if(shift?.branch?.alias) {
                title = `${shift?.branch?.alias}`;
            }else {
                title = `${shift?.branch?.company_name} (${shift.branch?.contact_tel}) \r\n ${shift?.branch?.street} ${shift?.branch?.number}, ${shift?.branch?.postal} ${shift?.branch?.city}`;
            }
        }

        const applicationIdOfFreelancer = shift.own_application_id ?? null;
        const idForEvent = shift?.id + '-' + (applicationIdOfFreelancer || 'none');
        if(!shift.has_contract && shift.applications_count === 0) {
            return {
                id: idForEvent,
                title: t('availabilities.available'),
                start: shift.start_time,
                end: shift.end_time,
                color: 'blue'
            }
        }

        if(shift.freelancer?.id === user.id) {
            phaseColor = 'green';

            if(shift?.branch?.alias) {
                title = `${shift?.branch?.company_name} (${shift.branch?.contact_tel}) \r\n ${shift?.branch?.street} ${shift?.branch?.number}, ${shift?.branch?.postal} ${shift?.branch?.city}`;
            }

        }

        if((shift?.freelancer?.id === null || shift?.freelancer?.id === undefined) && shift.applications_count > 0) {
            phaseColor = 'orange';
            title = t('availabilities.awaiting_customer_confirmation')
        }

        const showStatuut = shift.own_applied_as_statuut && (user?.extra_statuten?.length ?? 0) > 0;

        return {
            id: idForEvent,
            title: title,
            start: shift.start_time,
            end: shift.end_time,
            color: phaseColor,
            extendedProps: showStatuut ? {statuut: shift.own_applied_as_statuut} : {},
        }
    });

    const events = [...transformedAvailabilities, ...transformedShifts];

    const getShiftColor = (shift) => {
        if (shift.freelancer?.id === user.id) {
            return 'green';
        }
        if ((shift?.freelancer?.id === null || shift?.freelancer?.id === undefined) && shift.applications_count > 0) {
            return 'orange';
        }
        if (!shift.has_contract && shift.applications_count === 0) {
            return 'gray';
        }
        return 'lightblue';
    };

    const getColorClass = (color) => {
        switch(color) {
            case 'green':
                return 'border-green-500';
            case 'orange':
                return 'border-yellow-500';
            case 'gray':
                return 'border-gray-400';
            case 'lightblue':
                return 'border-blue-300';
            default:
                return 'border-blue-300';
        }
    };

    const handleShiftClick = (shift, availability = null) => {
        if (availability) {
            setAvailabilityObjectToEdit(availability);
            setOpenEdit(true);
            return;
        }

        const color = getShiftColor(shift);

        if (color === 'orange') {
            const applicationId = shift.own_application_id;
            if (applicationId) {
                navigate(`/freelancer/applications/${applicationId}`);
            }
        } else if (color === 'green') {
            navigate(`/freelancer/availabilities/shifts/${shift.id}`);
        }
    };

    const getShiftsGroupedByDay = () => {
        const startOfWeek = visibleDate.startOf('week');
        const daysInWeek = [];

        for (let i = 0; i < 7; i++) {
            const currentDay = startOfWeek.add(i, 'day');
            const dayShifts = availabilitiesShifts.filter((shift) =>
                dayjs(shift.start_time).format('YYYY-MM-DD') === currentDay.format('YYYY-MM-DD')
            );
            const dayAvailabilities = availabilities.filter((availability) =>
                dayjs(availability.start_time).format('YYYY-MM-DD') === currentDay.format('YYYY-MM-DD')
            );

            if (dayShifts.length > 0 || dayAvailabilities.length > 0) {
                daysInWeek.push({
                    date: currentDay,
                    shifts: dayShifts,
                    availabilities: dayAvailabilities
                });
            }
        }

        return daysInWeek;
    };

    const shiftsGroupedByDay = getShiftsGroupedByDay();

    return (
        <div>
            <div>
                <div className={'flex flex-row justify-between items-center mb-4 md:mb-0 px-4 md:px-0'}>
                    <h1 className={'text-2xl font-semibold'}>{t('availabilities.title')}</h1>
                    <button
                        onClick={() => setOpen(true)}
                        className={`rounded-md border border-solid border-black cursor-pointer px-3 py-1.5 md:px-4 md:py-2 text-xs md:text-sm font-semibold shadow-sm transition-all duration-300 ease-in-out bg-black text-white hover:text-black hover:bg-transparent`}
                    >
                        {t('availabilities.add_availability')}
                    </button>
                </div>
            </div>
            <div className={'grid grid-cols-1 xl:grid-cols-[300px_1fr] gap-4 p-4'}>
                <div className={'space-y-4'}>
                    <div className={'hidden xl:block calendar-styled-ccp'}>
                        <DatePicker
                            onSelect={(e) => {
                                calendarRef.current.getApi()?.gotoDate(e)
                                setVisibleDate(dayjs(e))
                                setDate(dayjs(e))
                            }}
                            onMonthChange={(date) => {setVisibleDate(dayjs(date))}}
                            renderDayContents={(day, date) => {
                                const hasSomethingOnThisDay = availabilitiesShifts.find((shift) => {
                                    return dayjs(shift.start_time).format('DD-MM-YYYY') === dayjs(date).format('DD-MM-YYYY')
                                });

                                if (hasSomethingOnThisDay) {
                                    return (
                                        <div className="relative">
                                            <span title={day}>{day}</span>
                                            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-secondary rounded-full"></div>
                                        </div>
                                    );
                                }

                                return <span title={day}>{day}</span>;
                            }}
                            inline={true}
                            showWeekPicker={true}
                            showMonthYearDropdown={false}
                            calendarStartDay={1}
                            locale={currentLang}
                        />
                    </div>

                    <BaseCard border={false} className={'hidden xl:block'}>
                        <h2 className={'text-xl font-semibold mb-4'}>{t('availabilities.week_overview')}</h2>

                        {shiftsGroupedByDay.length === 0 ? (
                            <div className={'text-center text-gray-500 py-8'}>
                                <p>{t('availabilities.empty_week')}</p>
                            </div>
                        ) : (
                            <div className={'space-y-4'}>
                                {shiftsGroupedByDay.map((day) => (
                                    <div key={day.date.format('YYYY-MM-DD')} className={'border-b border-gray-200 pb-4 last:border-b-0'}>
                                        <div className={'flex items-baseline mb-3'}>
                                            <h3 className={'text-lg font-semibold text-gray-900 mr-2'}>
                                                {day.date.format('dddd')}
                                            </h3>
                                            <span className={'text-sm text-gray-500'}>
                                                {day.date.format('D MMM')}
                                            </span>
                                        </div>

                                        <div className={'space-y-2'}>
                                            {day.availabilities.map((availability) => (
                                                <div
                                                    key={`availability-${availability.id}`}
                                                    onClick={() => handleShiftClick(null, availability)}
                                                    className={'flex items-start cursor-pointer hover:bg-gray-50 rounded-lg p-3 transition-colors duration-200 border-l-4 border-blue-300'}
                                                >
                                                    <div className={'flex-1'}>
                                                        <div className={'flex items-center'}>
                                                            <span className={'text-sm font-medium text-gray-900'}>
                                                                {dayjs(availability.start_time).format('HH:mm')} - {dayjs(availability.end_time).format('HH:mm')}
                                                            </span>
                                                        </div>
                                                        <div className={'mt-1'}>
                                                            <p className={'text-sm text-gray-600'}>{t('availabilities.available')}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}

                                            {day.shifts.map((shift) => {
                                                const color = getShiftColor(shift);
                                                const colorClass = getColorClass(color);

                                                let title = '';
                                                let subtitle = '';

                                                if (shift?.branch) {
                                                    if (shift?.branch?.alias) {
                                                        title = shift?.branch?.alias;
                                                        subtitle = `${shift?.branch?.company_name} - ${shift?.branch?.street} ${shift?.branch?.number}, ${shift?.branch?.postal} ${shift?.branch?.city}`;
                                                    } else {
                                                        title = shift?.branch?.company_name;
                                                        subtitle = `${shift?.branch?.street} ${shift?.branch?.number}, ${shift?.branch?.postal} ${shift?.branch?.city}`;
                                                    }
                                                }

                                                if (color === 'orange' && !title) {
                                                    title = t('availabilities.awaiting_confirmation');
                                                } else if (color === 'gray' && !title) {
                                                    title = t('availabilities.available');
                                                    subtitle = t('availabilities.no_applications');
                                                }

                                                const isMulti = (user?.extra_statuten?.length ?? 0) > 0;
                                                const statuutInfo = (color === 'green' && shift.own_applied_as_statuut && isMulti)
                                                    ? {
                                                        freelance: {label: 'Freelance', cls: 'bg-gray-100 text-gray-700'},
                                                        interim: {label: 'Interim', cls: 'bg-purple-100 text-purple-800'},
                                                        student: {label: 'Student', cls: 'bg-blue-100 text-blue-800'},
                                                        flexi_job: {label: 'Flexi-job', cls: 'bg-yellow-100 text-yellow-800'},
                                                    }[shift.own_applied_as_statuut] ?? null
                                                    : null;

                                                return (
                                                    <div
                                                        key={`shift-${shift.id}`}
                                                        onClick={() => handleShiftClick(shift)}
                                                        className={`flex items-start cursor-pointer hover:bg-gray-50 rounded-lg p-3 transition-colors duration-200 border-l-4 ${colorClass}`}
                                                    >
                                                        <div className={'flex-1'}>
                                                            <div className={'flex items-center gap-2'}>
                                                                <span className={'text-sm font-medium text-gray-900'}>
                                                                    {dayjs(shift.start_time).format('HH:mm')} - {dayjs(shift.end_time).format('HH:mm')}
                                                                </span>
                                                                {statuutInfo && (
                                                                    <span className={`text-xs font-semibold rounded px-1.5 py-0.5 ${statuutInfo.cls}`}>{statuutInfo.label}</span>
                                                                )}
                                                            </div>
                                                            <div className={'mt-1'}>
                                                                <p className={'text-sm font-medium text-gray-800'}>{title}</p>
                                                                {subtitle && (
                                                                    <p className={'text-xs text-gray-600 mt-0.5'}>{subtitle}</p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </BaseCard>
                </div>

                <div>
                    <div className={'xl:hidden mb-4'}>
                        <div className={"flex flex-row justify-between items-center"}>
                            <div>
                                <button
                                    onClick={() => {
                                        const newDateForCalendarThatsVisible = dayjs(calendarRef.current?.getApi()?.getDate()).subtract(1, 'week');
                                        calendarRef.current?.getApi()?.prev()
                                        setDate(newDateForCalendarThatsVisible)
                                        setVisibleDate(newDateForCalendarThatsVisible)
                                    }}
                                    className={`rounded-md border border-solid border-black cursor-pointer px-4 py-2 text-sm font-semibold shadow-sm transition-all duration-300 ease-in-out bg-black text-white hover:text-black hover:bg-transparent`}
                                >
                                    <FontAwesomeIcon icon={faChevronLeft} className={'m-auto'}/>
                                </button>
                            </div>

                            <div className={'my-auto flex-1 text-center'}>
                                {
                                    calendarRef.current?.getApi()?.view?.title && (
                                        <p>{calendarRef.current.getApi()?.view?.title}</p>
                                    )
                                }
                            </div>

                            <div>
                                <button
                                    onClick={() => {
                                        const newDateForCalendarThatsVisible = dayjs(calendarRef.current?.getApi()?.getDate()).add(1, 'week');
                                        calendarRef.current?.getApi()?.next()
                                        setDate(newDateForCalendarThatsVisible)
                                        setVisibleDate(newDateForCalendarThatsVisible)
                                    }}
                                    className={`rounded-md border border-solid border-black cursor-pointer px-4 py-2 text-sm font-semibold shadow-sm transition-all duration-300 ease-in-out bg-black text-white hover:text-black hover:bg-transparent`}
                                >
                                    <FontAwesomeIcon icon={faChevronRight} className={'m-auto'}/>
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className={'hidden xl:flex flex-row justify-between items-center mb-4'}>
                        <div>
                            <button
                                onClick={() => {
                                    const newDateForCalendarThatsVisible = dayjs(calendarRef.current?.getApi()?.getDate()).subtract(1, 'week');
                                    calendarRef.current?.getApi()?.prev()
                                    setDate(newDateForCalendarThatsVisible)
                                    setVisibleDate(newDateForCalendarThatsVisible)
                                }}
                                className={`rounded-md border border-solid border-black cursor-pointer px-4 py-2 text-sm font-semibold shadow-sm transition-all duration-300 ease-in-out bg-black text-white hover:text-black hover:bg-transparent`}
                            >
                                <FontAwesomeIcon icon={faChevronLeft} className={'m-auto'}/>
                            </button>
                        </div>

                        <div className={'my-auto flex-1 text-center'}>
                            {
                                calendarRef.current?.getApi()?.view?.title && (
                                    <p className={'font-semibold'}>{calendarRef.current.getApi()?.view?.title}</p>
                                )
                            }
                        </div>

                        <div>
                            <button
                                onClick={() => {
                                    const newDateForCalendarThatsVisible = dayjs(calendarRef.current?.getApi()?.getDate()).add(1, 'week');
                                    calendarRef.current?.getApi()?.next()
                                    setDate(newDateForCalendarThatsVisible)
                                    setVisibleDate(newDateForCalendarThatsVisible)
                                }}
                                className={`rounded-md border border-solid border-black cursor-pointer px-4 py-2 text-sm font-semibold shadow-sm transition-all duration-300 ease-in-out bg-black text-white hover:text-black hover:bg-transparent`}
                            >
                                <FontAwesomeIcon icon={faChevronRight} className={'m-auto'}/>
                            </button>
                        </div>
                    </div>

                    <BaseCard border={false} className={'xl:mb-0 mb-4'}>
                        <div className={'xl:hidden flex justify-end mb-2'}>
                            <button
                                onClick={() => setIsCalendarCollapsed(!isCalendarCollapsed)}
                                className={`rounded-md border border-solid border-gray-300 cursor-pointer px-3 py-1 text-sm font-semibold shadow-sm transition-all duration-300 ease-in-out bg-white text-gray-700 hover:bg-gray-50`}
                            >
                                <FontAwesomeIcon icon={isCalendarCollapsed ? faChevronDown : faChevronUp} className={'mr-2'}/>
                                {isCalendarCollapsed ? t('availabilities.show_calendar') : t('availabilities.hide_calendar')}
                            </button>
                        </div>

                        <div className={isCalendarCollapsed ? 'xl:block hidden' : ''}>
                            <FullCalendar
                                ref={calendarRef}
                                plugins={[dayGridPlugin, timeGridPlugin]}
                                initialView="timeGridWeek"
                                initialDate={new Date()}
                                locales={[nlLocale, frLocale]}
                                locale={currentLang}
                                allDaySlot={false}
                                headerToolbar={false}
                                eventTimeFormat={{hour: "2-digit", minute: "2-digit", hour12: false}}
                                slotLabelFormat={{hour: "2-digit", minute: "2-digit", hour12: false}}
                                events={events}
                                firstDay={1}
                                displayEventEnd={true}
                                eventContent={(arg) => {
                                    const {statuut} = arg.event.extendedProps || {};
                                    const lines = (arg.event.title || '').split(/\r?\n/);
                                    const badgeMap = {
                                        freelance: {label: 'F', cls: 'bg-gray-100 text-gray-700'},
                                        interim: {label: 'I', cls: 'bg-purple-100 text-purple-800'},
                                        student: {label: 'S', cls: 'bg-blue-100 text-blue-800'},
                                        flexi_job: {label: 'X', cls: 'bg-yellow-100 text-yellow-800'},
                                    };
                                    const badge = statuut ? badgeMap[statuut] : null;
                                    const isMobile = window.innerWidth < 768;
                                    return (
                                        <div className="fc-event-custom overflow-hidden text-xs leading-tight p-0.5">
                                            <div className="font-semibold">{arg.timeText}</div>
                                            {isMobile
                                                ? <div className="truncate">{lines[0]}</div>
                                                : lines.map((line, i) => <div key={i}>{line}</div>)
                                            }
                                            {badge && (
                                                <span className={`inline-block mt-1 px-1.5 py-0.5 rounded text-[10px] font-bold ${badge.cls}`}>{badge.label}</span>
                                            )}
                                        </div>
                                    );
                                }}
                                eventClick={(info) => {
                                    if(info.event.backgroundColor === 'orange') {
                                        const applicationId = info.event.id.split('-')[1];
                                        if(applicationId === 'none') {
                                            return;
                                        }
                                        navigate(`/freelancer/applications/${applicationId}`);
                                    }

                                    if(info.event.backgroundColor === 'green') {
                                        const shiftId = info.event.id.split('-')[0];
                                        navigate(`/freelancer/availabilities/shifts/${shiftId}`);
                                    }

                                    if(info.event.backgroundColor === 'lightblue') {
                                        setAvailabilityObjectToEdit(availabilities.find((availabilities) => availabilities.id === parseInt(info.event.id)))
                                        setOpenEdit(true)
                                    }
                                }}
                            />

                        </div>

                        <div className={'flex flex-wrap space-y-4 mt-4'}>
                            <div className={'flex flex-row w-full my-auto'}>
                                <div className={'w-6 h-6 bg-green-500 mr-4 rounded-full my-auto'}></div>
                                <p className={'my-auto'}>{t('availabilities.candidacy_confirmed')}</p>
                            </div>

                            <div className={'flex flex-row w-full align-top'}>
                                <div className={'w-6 h-6 bg-yellow-500 mr-4 rounded-full'}></div>
                                <p className={'my-auto'}>{t('availabilities.awaiting_customer_confirmation')}</p>
                            </div>

                            <div className={'flex flex-row'}>
                                <div className={'w-6 h-6 bg-blue-300 mr-4 rounded-full'}></div>
                                <p className={'my-auto'}>{t('availabilities.available')}</p>
                            </div>
                        </div>
                    </BaseCard>

                <BaseCard border={false} className={'xl:hidden mt-4'}>
                    <h2 className={'text-xl font-semibold mb-4'}>{t('availabilities.week_overview')}</h2>

                    {shiftsGroupedByDay.length === 0 ? (
                        <div className={'text-center text-gray-500 py-8'}>
                            <p>{t('availabilities.empty_week')}</p>
                        </div>
                    ) : (
                        <div className={'space-y-4'}>
                            {shiftsGroupedByDay.map((day) => (
                                <div key={day.date.format('YYYY-MM-DD')} className={'border-b border-gray-200 pb-4 last:border-b-0'}>
                                    <div className={'flex items-baseline mb-3'}>
                                        <h3 className={'text-lg font-semibold text-gray-900 mr-2'}>
                                            {day.date.format('dddd')}
                                        </h3>
                                        <span className={'text-sm text-gray-500'}>
                                            {day.date.format('D MMM')}
                                        </span>
                                    </div>

                                    <div className={'space-y-2'}>
                                        {day.availabilities.map((availability) => (
                                            <div
                                                key={`availability-${availability.id}`}
                                                onClick={() => handleShiftClick(null, availability)}
                                                className={'flex items-start cursor-pointer hover:bg-gray-50 rounded-lg p-3 transition-colors duration-200 border-l-4 border-blue-300'}
                                            >
                                                <div className={'flex-1'}>
                                                    <div className={'flex items-center'}>
                                                        <span className={'text-sm font-medium text-gray-900'}>
                                                            {dayjs(availability.start_time).format('HH:mm')} - {dayjs(availability.end_time).format('HH:mm')}
                                                        </span>
                                                    </div>
                                                    <div className={'mt-1'}>
                                                        <p className={'text-sm text-gray-600'}>{t('availabilities.available')}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}

                                        {day.shifts.map((shift) => {
                                            const color = getShiftColor(shift);
                                            const colorClass = getColorClass(color);

                                            let title = '';
                                            let subtitle = '';

                                            if (shift?.branch) {
                                                if (shift?.branch?.alias) {
                                                    title = shift?.branch?.alias;
                                                    subtitle = `${shift?.branch?.company_name} - ${shift?.branch?.street} ${shift?.branch?.number}, ${shift?.branch?.postal} ${shift?.branch?.city}`;
                                                } else {
                                                    title = shift?.branch?.company_name;
                                                    subtitle = `${shift?.branch?.street} ${shift?.branch?.number}, ${shift?.branch?.postal} ${shift?.branch?.city}`;
                                                }
                                            }

                                            if (color === 'orange' && !title) {
                                                title = t('availabilities.awaiting_confirmation');
                                            } else if (color === 'gray' && !title) {
                                                title = t('availabilities.available');
                                                subtitle = t('availabilities.no_applications');
                                            }

                                            return (
                                                <div
                                                    key={`shift-${shift.id}`}
                                                    onClick={() => handleShiftClick(shift)}
                                                    className={`flex items-start cursor-pointer hover:bg-gray-50 rounded-lg p-3 transition-colors duration-200 border-l-4 ${colorClass}`}
                                                >
                                                    <div className={'flex-1'}>
                                                        <div className={'flex items-center'}>
                                                            <span className={'text-sm font-medium text-gray-900'}>
                                                                {dayjs(shift.start_time).format('HH:mm')} - {dayjs(shift.end_time).format('HH:mm')}
                                                            </span>
                                                        </div>
                                                        <div className={'mt-1'}>
                                                            <p className={'text-sm font-medium text-gray-800'}>{title}</p>
                                                            {subtitle && (
                                                                <p className={'text-xs text-gray-600 mt-0.5'}>{subtitle}</p>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </BaseCard>
                </div>

                <CreateAvailabilityDialog open={open} onClose={() => {
                    getAvailabilitiesAndShifts(date.unix())
                    setOpen(false)
                }} userId={user?.id}/>

                <UpdateAvailabilityDialog open={openEdit} onClose={() => {
                    setOpenEdit(false)
                    setAvailabilityObjectToEdit(null)
                    getAvailabilitiesAndShifts(date.unix())
                }} userId={user?.id} availability={availabilityObjectToEdit}/>
            </div>
        </div>
    )
}